import React, { useCallback, useContext, useEffect, useState } from 'react';

import styled from 'styled-components';

import { coreOrderFieldsSchemaSelectors } from '@cd3p/core/selectors';
import {
  APICustomFieldType,
  APIOrderFieldDefinitionRequestModel,
  APIOrderFieldsSchemaModel,
  APIOrderFieldsSchemaRequestModel,
} from '@cd3p/core/types/api';
import { Button, Typography } from '@mui/material';
import {
  CircularProgress,
  ConfirmationDialog,
  Modal,
  Stack,
  SvgIcon,
} from 'components';
import { useHandleApiResult } from 'hooks/useHandleApiResult';
import { LocationChangeConfirmationContext } from 'providers/LocationChangeConfirmationProvider';
import { FormProvider, useForm } from 'react-hook-form';

import {
  CUSTOM_ORDER_FIELDS_FORM_KEY,
  CustomOrderFieldFormFields,
  CustomOrderFieldFormT,
  OrderFieldsSchemaForm,
} from './OrderFieldsSchemaForm';

import { useBeforeUnload, useSelector, useTranslation } from 'third-party';

import { useOrderFieldsSchema } from 'modules/orderFieldsSchema';

import { appSelectors } from 'selectors';

import { OrderCustomFieldsEdit } from 'features/OrderView/OrderCustomFieldsEdit';

const Wrapper = styled(Stack)`
  align-items: center;
  min-height: 0;
  overflow-y: auto;
`;

const ContentContainer = styled(Stack)`
  flex: 1;
  width: 65rem;
  min-height: 0;
  margin-top: 1.5rem;
  padding-bottom: 1rem;
`;

const Header = styled(Typography)`
  font-weight: 700;
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.custom.palette.secondary900};
`;

const Description = styled(Typography)`
  margin-bottom: 2rem;
  color: ${props => props.theme.custom.palette.secondary700};
`;

const Footer = styled.div`
  padding: 1.5rem 0;
  border-top: 1px solid ${props => props.theme.custom.palette.secondary200};
  margin-top: 2rem;
  position: sticky;
  bottom: 0;
  background-color: ${props => props.theme.palette.background.default};
`;

const WrapperFromPreview = styled.div`
  background-color: #bbbab9;
  width: 339px;
  padding-bottom: 1rem;
`;

const WrapperFormPreviewInner = styled.div`
  padding: 1rem 1rem 1.5rem 1rem;
  background-color: #fff;
  margin: 0 1rem;
  border-radius: 10px;
`;

const PublishButton = styled(Button)`
  min-width: 120px;
`;

const FormPreviewBlock = styled.div`
  width: 352px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
`;

type CustomOrderFieldsFormWrapperT = {
  [CUSTOM_ORDER_FIELDS_FORM_KEY]: Array<CustomOrderFieldFormT>;
};

type OrderFieldsSchemaTabProps = {
  providerId?: string;
};

export const OrderFieldsSchemaTab: React.FC<OrderFieldsSchemaTabProps> = ({
  providerId,
}) => {
  const { t } = useTranslation();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLeaveConfirmationDialogShown, setLeaveConfirmationDialogShown] =
    useState(false);
  const handleApiResult = useHandleApiResult();
  const userProviderId = useSelector(appSelectors.providerId);
  const schemaProviderId = providerId || userProviderId;

  const { loadOrderFieldsSchema, updateOrderFieldsSchema } =
    useOrderFieldsSchema(schemaProviderId);
  const orderFieldsSchemaLoading = useSelector(
    coreOrderFieldsSchemaSelectors.loadOrderFieldsSchemaPending,
  );

  const { continueChangeLocation, addLocationChangeConfirmation } = useContext(
    LocationChangeConfirmationContext,
  );

  const formMethods = useForm<CustomOrderFieldsFormWrapperT>({
    mode: 'onChange',
    defaultValues: { [CUSTOM_ORDER_FIELDS_FORM_KEY]: [] },
  });

  const {
    reset,
    watch,
    formState: { isDirty, isSubmitting, isValid },
  } = formMethods;

  useBeforeUnload(isDirty, t('common.exitConfirmationWindow.text'));

  const fields = watch(CUSTOM_ORDER_FIELDS_FORM_KEY);
  const hasFields = fields.length > 0;

  const previewFields = fields.map(field => ({
    label: field.label,
    name: field.label,
    type: APICustomFieldType.Text,
    placeholder: field.placeholder,
    companyIds: field.customers
      ? field.customers.map(customer => customer.value)
      : [],
  }));

  useEffect(() => {
    handleApiResult<APIOrderFieldsSchemaModel>(
      () => loadOrderFieldsSchema(),
      ({ result }) => {
        const mappedData: CustomOrderFieldFormT[] = (
          result?.payload?.fields || []
        ).map(field => ({
          [CustomOrderFieldFormFields.Label]: field.label,
          [CustomOrderFieldFormFields.Placeholder]: field.placeholder,
          [CustomOrderFieldFormFields.ApplyToAllCustomers]:
            !field.companies || field.companies?.length === 0,
          [CustomOrderFieldFormFields.Customers]: (field.companies || []).map(
            company => ({
              label: company.name,
              value: company.id,
            }),
          ),
        }));

        reset({ [CUSTOM_ORDER_FIELDS_FORM_KEY]: mappedData });
      },
    );
  }, [loadOrderFieldsSchema, handleApiResult, schemaProviderId, reset]);

  const handlePublish = async (data: CustomOrderFieldsFormWrapperT) => {
    if (!schemaProviderId) return;

    const requestFields: APIOrderFieldDefinitionRequestModel[] = data[
      CUSTOM_ORDER_FIELDS_FORM_KEY
    ].map(field => ({
      label: field.label,
      type: APICustomFieldType.Text,
      placeholder: field.placeholder,
      companyIds: field.customers
        ? field.customers.map(customer => customer.value)
        : [],
    }));

    const requestPayload: APIOrderFieldsSchemaRequestModel = {
      fields: requestFields,
    };

    await handleApiResult(
      () => updateOrderFieldsSchema(requestPayload),
      ({ showSuccessToast }) => {
        reset(data);
        showSuccessToast(t('settings.orders.customFields.success'));
      },
      ({ showErrorToast }) => {
        showErrorToast();
      },
    );
  };

  const onSubmit = async (data: CustomOrderFieldsFormWrapperT) => {
    await handlePublish(data);
  };

  const onLocationChange = useCallback(() => {
    if (isDirty) {
      setLeaveConfirmationDialogShown(true);
      return false;
    }
  }, [isDirty]);

  useEffect(() => {
    return addLocationChangeConfirmation(onLocationChange);
  }, [addLocationChangeConfirmation, onLocationChange]);

  if (orderFieldsSchemaLoading) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Wrapper>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <ContentContainer>
            <Header variant="subtitle2">
              {t('settings.orders.customFields.title')}
            </Header>
            <Description>
              {t('settings.orders.customFields.description')}
            </Description>
            <OrderFieldsSchemaForm />
          </ContentContainer>

          <Footer>
            <PublishButton
              type="submit"
              color="primary"
              variant="contained"
              disabled={!isDirty || !isValid || isSubmitting}
              loading={isSubmitting}
            >
              {t('settings.orders.customFields.publish')}
            </PublishButton>

            {hasFields && (
              <Button onClick={() => setIsPreviewOpen(true)}>
                {t('settings.orders.customFields.preview')}
              </Button>
            )}
          </Footer>
        </form>
        <Modal
          open={isPreviewOpen}
          cancelEnabled={false}
          header={t('settings.orders.customFields.previewModal')}
          isDisabled={orderFieldsSchemaLoading}
          isPending={orderFieldsSchemaLoading}
          isConfirmDisabled={!isValid || !isDirty}
          onClose={() => setIsPreviewOpen(false)}
          width="50rem"
          contentWrapperStyle={{ overflow: 'scroll-y' }}
        >
          <Typography variant="body1" mb={2}>
            {t('settings.orders.customFields.previewModalText')}
          </Typography>
          <FormPreviewBlock>
            <SvgIcon icon="form-preview-up" height="170px" width="352px" />
            <WrapperFromPreview>
              <WrapperFormPreviewInner>
                <OrderCustomFieldsEdit isDisabled orderFields={previewFields} />
              </WrapperFormPreviewInner>
              <SvgIcon icon="form-preview-down" height="164px" width="339px" />
            </WrapperFromPreview>
          </FormPreviewBlock>
        </Modal>

        <ConfirmationDialog
          onClose={() => setLeaveConfirmationDialogShown(false)}
          open={isLeaveConfirmationDialogShown}
          handleActionClick={() => {
            continueChangeLocation();
            setLeaveConfirmationDialogShown(false);
          }}
          description={t('common.exitConfirmationWindow.text')}
          cancelButtonTitle={t('common.continueEditing')}
          actionButtonTitle={t('common.confirmCancel')}
        />
      </FormProvider>
    </Wrapper>
  );
};
